import React, { useState, useEffect } from 'react';
import './QuoteRequestForm.css';
import { useLanguage } from '../context/LanguageContext';
import cf7Config from '../data/cf7Config';

const CartQuoteRequestForm = ({ isOpen, onClose, cartItems, totalPrice }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    remarks: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Reset form when it's opened and manage body class
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        remarks: ''
      });
      setErrors({});
      setIsSubmitted(false);
      
      // Add class to body when modal is open
      document.body.classList.add('modal-open');
    } else {
      // Remove class from body when modal is closed
      document.body.classList.remove('modal-open');
    }
    
    // Cleanup function to ensure class is removed when component unmounts
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};
    
    // Validate name (required)
    if (!formData.name.trim()) {
      newErrors.name = t('requiredField');
    }
    
    // Validate company (required)
    if (!formData.company.trim()) {
      newErrors.company = t('requiredField');
    }
    
    // Validate email (required and format)
    if (!formData.email.trim()) {
      newErrors.email = t('requiredField');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('invalidEmail');
    }
    
    // Validate phone (required)
    if (!formData.phone.trim()) {
      newErrors.phone = t('requiredField');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to parse prices correctly
  const parsePrice = (priceValue) => {
    if (!priceValue) return 0;
    
    // Convert to string first
    const priceStr = String(priceValue);
    
    // Remove currency symbols and spaces
    let cleanPrice = priceStr.replace(/[€$£\s]/g, '');
    
    // Handle Dutch format (1.000,00)
    if (cleanPrice.includes(',') && cleanPrice.includes('.')) {
      // Dutch format: 1.000,00 -> 1000.00
      cleanPrice = cleanPrice.replace(/\./g, '').replace(',', '.');
    }
    // Handle comma as decimal separator (1,00)
    else if (cleanPrice.includes(',') && !cleanPrice.includes('.')) {
      cleanPrice = cleanPrice.replace(',', '.');
    }
    
    const parsed = parseFloat(cleanPrice);
    
    // If the number seems too small (less than 1), it might be in centen
    // Check if it's likely a cent value (e.g., 1000 for €10.00)
    if (parsed > 0 && parsed < 1000 && !cleanPrice.includes('.')) {
      return parsed / 100; // Convert from centen to euros
    }
    
    return parsed;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);

      // Prepare data for CF7
      const formDataToSend = new FormData();
      formDataToSend.append('_wpcf7', cf7Config.formNr);
      formDataToSend.append('_wpcf7_version', cf7Config.wpcf7Version);
      formDataToSend.append('_wpcf7_locale', cf7Config.wpcf7Locale);
      formDataToSend.append('_wpcf7_unit_tag', cf7Config.formId);
      formDataToSend.append('_wpcf7_container_post', cf7Config.containerPost);
      formDataToSend.append('_wpcf7_posted_data_hash', '');
      formDataToSend.append('your-name', formData.name);
      formDataToSend.append('your-company', formData.company);
      formDataToSend.append('your-email', formData.email);
      formDataToSend.append('your-tel', formData.phone);
      formDataToSend.append('remarks', formData.remarks);
      
      // Prepare cart items with all export fields
      const cartItemsForCF7 = cartItems.map(item => {
        const impressions = item.impressions || (item.bereik ? (parseFloat(item.bereik.replace(/\./g, '').replace(',', '.')) * 1000) : '');
        const bereikValue = item.bereik ? item.bereik : (impressions ? (impressions / 1000) : '');
        
        return {
          titel: item.title || '',
          medium: item.medium || '',
          mediumType: item.originalData?.Mediumtype || '',
          positie: item.position || '',
          formaat: item.format || '',
          bereik: bereikValue,
          aantal: '1',
          basisprijs: item.basePrice || '',
          posFactor: item.positionFactor || '',
          formFactor: item.formatFactor || '',
          targeting: item.targetingFactor || '',
          plaatsingsdatum: item.plaatsingsdatum || '',
          seizoensFactor: '1,0',
          brutoTotaal: item.price || '',
          korting: item.discountPercentage || 0,
          nettoTotaal: item.calculatedPrice || item.price || '',
          // Additional fields for reference
          originalPrice: item.originalPrice || item.price,
          discountPercentage: item.discountPercentage || 0,
          impressions: impressions
        };
      });
      
      // Format cart items as text to match the form display exactly
      let formattedCartItems = '';
      cartItems.forEach((item, index) => {
        // Debug logging for cart-item-summary
        console.log(`Cart Item Summary ${index + 1}:`, {
          title: item.title,
          format: item.format,
          medium: item.medium,
          discountPercentage: item.discountPercentage,
          originalPrice: item.originalPrice,
          calculatedPrice: item.calculatedPrice,
          price: item.price,
          bereik: item.bereik,
          // Debug price parsing
          originalPriceType: typeof item.originalPrice,
          priceType: typeof item.price,
          originalPriceRaw: item.originalPrice,
          priceRaw: item.price
        });
        
        formattedCartItems += `${item.title || 'Unknown'}\n`;
        formattedCartItems += `${item.format || 'Unknown'} ${item.medium ? `- ${item.medium}` : ''}\n`;
        
        // Calculate the correct final price with discount
        const originalPrice = parsePrice(item.originalPrice || item.price);
        const discountPercentage = parseFloat(item.discountPercentage || 0);
        const finalPrice = originalPrice - (originalPrice * (discountPercentage / 100));
        
        // Debug the calculation
        console.log(`Price calculation for item ${index + 1}:`, {
          originalPriceRaw: item.originalPrice || item.price,
          originalPriceParsed: originalPrice,
          discountPercentage: discountPercentage,
          finalPrice: finalPrice,
          isNaN: isNaN(originalPrice) || isNaN(finalPrice)
        });
        
        if (item.discountPercentage) {
          formattedCartItems += `€ ${originalPrice.toFixed(2)} → € ${finalPrice.toFixed(2)} (${discountPercentage}% korting)\n`;
        } else {
          formattedCartItems += `€ ${originalPrice.toFixed(2)}\n`;
        }
        
        if (item.bereik) {
          const bereikValue = (parseFloat(item.bereik.replace(/\./g, '').replace(',', '.')) * 1000).toLocaleString('de-DE');
          formattedCartItems += `Campagne bereik: ${bereikValue}\n`;
        }
        
        formattedCartItems += '\n';
      });
      
      formattedCartItems += `Totaal: € ${totalPrice}`;
      
      // Debug: log the final formatted text
      console.log('Formatted cart items for email:', formattedCartItems);

      formDataToSend.append('cartItems', formattedCartItems);
      formDataToSend.append('cartItemsJson', JSON.stringify(cartItemsForCF7));
      formDataToSend.append('totalPrice', totalPrice);
      
      // Add discount information
      const itemsWithDiscounts = cartItems.filter(item => item.discountPercentage);
      const totalDiscountAmount = itemsWithDiscounts.reduce((total, item) => {
        const originalPrice = parsePrice(item.originalPrice || item.price);
        const discountAmount = originalPrice * (parseFloat(item.discountPercentage) / 100);
        return total + discountAmount;
      }, 0);
      
      formDataToSend.append('hasDiscounts', itemsWithDiscounts.length > 0 ? 'Ja' : 'Nee');
      formDataToSend.append('totalDiscountAmount', `€ ${totalDiscountAmount.toFixed(2)}`);
      formDataToSend.append('discountedItemsCount', itemsWithDiscounts.length.toString());
      
      // Add individual discount details
      const discountDetails = itemsWithDiscounts.map((item, index) => {
        const originalPrice = parsePrice(item.originalPrice || item.price);
        const finalPrice = originalPrice - (originalPrice * (parseFloat(item.discountPercentage) / 100));
        return `${index + 1}. ${item.title}: € ${originalPrice.toFixed(2)} → € ${finalPrice.toFixed(2)} (${item.discountPercentage}% korting)`;
      }).join('\n');
      
      formDataToSend.append('discountDetails', discountDetails);
      formDataToSend.append('timestamp', new Date().toISOString());

      // POST to the CF7 endpoint (adjust URL to your WP site)
      try {
        const response = await fetch('/wp-json/contact-form-7/v1/contact-forms/' + cf7Config.formNr + '/feedback', {
          method: 'POST',
          body: formDataToSend,
        });
        const data = await response.json();
        if (data.status === 'mail_sent') {
          setIsSubmitting(false);
          setIsSubmitted(true);
          setTimeout(() => {
            onClose();
          }, 3000);
        } else {
          setIsSubmitting(false);
          setErrors({ submit: t('errorSendingForm') });
        }
      } catch (error) {
        setIsSubmitting(false);
        setErrors({ submit: t('errorSendingForm') });
      }
    }
  };

  // If the form is not open, don't render anything
  if (!isOpen) return null;

  return (
    <div className="quote-request-overlay" onClick={onClose}>
      <div className="quote-request-modal" onClick={e => e.stopPropagation()}>
        <div className="quote-form-header">
          <img 
            src={`${process.env.PUBLIC_URL}/assets/logo-tariefcalculator.svg`} 
            alt="Mediahuis Tariefcalculator" 
            className="quote-form-logo"
          />
        </div>
        <button className="close-button" onClick={onClose} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="white"/>
          </svg>
        </button>
        
        <div className="quote-form-content">
          {!isSubmitted ? (
            <>
              <h2 className="quote-request-title">{t('quoteFormTitle')}</h2>
              
              <div className="product-summary cart-summary-list">
                <h3>{t('cartSummary')}</h3>
                {cartItems.map((item, index) => {
                  // Debug logging for cart-item-summary
                  console.log(`Cart Item Summary ${index + 1}:`, {
                    title: item.title,
                    format: item.format,
                    medium: item.medium,
                    discountPercentage: item.discountPercentage,
                    originalPrice: item.originalPrice,
                    calculatedPrice: item.calculatedPrice,
                    price: item.price,
                    bereik: item.bereik,
                    // Debug price parsing
                    originalPriceType: typeof item.originalPrice,
                    priceType: typeof item.price,
                    originalPriceRaw: item.originalPrice,
                    priceRaw: item.price
                  });
                  
                  // Calculate the correct final price with discount
                  const originalPrice = parsePrice(item.originalPrice || item.price);
                  const discountPercentage = parseFloat(item.discountPercentage || 0);
                  const finalPrice = originalPrice - (originalPrice * (discountPercentage / 100));
                  
                  // Debug the calculation
                  console.log(`Price calculation for item ${index + 1}:`, {
                    originalPriceRaw: item.originalPrice || item.price,
                    originalPriceParsed: originalPrice,
                    discountPercentage: discountPercentage,
                    finalPrice: finalPrice,
                    isNaN: isNaN(originalPrice) || isNaN(finalPrice)
                  });
                  
                  return (
                    <div key={index} className="cart-item-summary">
                      <p><strong>{item.title || 'Unknown'}</strong></p>
                      <p>{item.format || 'Unknown'} {item.medium ? `- ${item.medium}` : ''}</p>
                      <p>
                        {item.discountPercentage 
                          ? <span>
                              <span className="original-price">€ {originalPrice.toFixed(2)}</span>
                              {' → '}
                              <span className="final-price">€ {finalPrice.toFixed(2)}</span>
                              <span style={{color: '#28a745', fontSize: '0.9em', marginLeft: '8px'}}>
                                ({discountPercentage}% korting)
                              </span>
                            </span>
                          : <span>€ {originalPrice.toFixed(2)}</span>
                        }
                      </p>
                      {item.bereik && <p>{t('campaignReach')}: {(parseFloat(item.bereik.replace(/\./g, '').replace(',', '.')) * 1000).toLocaleString('de-DE')}</p>}
                    </div>
                  );
                })}
                <div className="cart-total-summary">
                  <p><strong>{t('total')}: € {totalPrice}</strong></p>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="quote-form">
                {/* Hidden CF7 fields */}
                <input type="hidden" name="_wpcf7" value={cf7Config.formNr} />
                <input type="hidden" name="_wpcf7_version" value={cf7Config.wpcf7Version} />
                <input type="hidden" name="_wpcf7_locale" value={cf7Config.wpcf7Locale} />
                <input type="hidden" name="_wpcf7_unit_tag" value={cf7Config.formId} />
                <input type="hidden" name="_wpcf7_container_post" value={cf7Config.containerPost} />
                <input type="hidden" name="_wpcf7_posted_data_hash" value="" />
                
                <div className="form-group">
                  <label htmlFor="name">{t('name')}*</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={errors.name ? 'error' : ''}
                    />
                    {errors.name && (
                      <>
                        <div className="info-icon"></div>
                        <div className="error-message">{errors.name}</div>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="company">{t('company')}*</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={errors.company ? 'error' : ''}
                    />
                    {errors.company && (
                      <>
                        <div className="info-icon"></div>
                        <div className="error-message">{errors.company}</div>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">{t('email')}*</label>
                  <div className="input-wrapper">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && (
                      <>
                        <div className="info-icon"></div>
                        <div className="error-message">{errors.email}</div>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">{t('phone')}*</label>
                  <div className="input-wrapper">
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={errors.phone ? 'error' : ''}
                    />
                    {errors.phone && (
                      <>
                        <div className="info-icon"></div>
                        <div className="error-message">{errors.phone}</div>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="remarks">{t('remarks')}</label>
                  <div className="input-wrapper">
                    <textarea
                      id="remarks"
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleChange}
                      rows="4"
                    />
                  </div>
                </div>
                
                <div className="form-actions">
                  <button type="button" className="cancel-button" onClick={onClose}>
                    {t('cancel')}
                  </button>
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '...' : t('submit')}
                  </button>
                </div>
                {errors.submit && (
                  <div className="error-message submit-error">
                    {errors.submit}
                  </div>
                )}
              </form>
            </>
          ) : (
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h2>{t('successMessage')}</h2>
              <p>{t('weWillContactYou')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartQuoteRequestForm;